define((require) => {
    "use strict";

    const _                     = require("underscore");
    const $                     = require("jquery");
    const Browser               = require("shared/services/Browser");
    const Class                 = require("shared/util/Class");
    const FontConstants         = require("shared/util/FontConstants");
    const FontProvider          = require("shared/data/fonts/FontProvider");
    const Preconditions         = require("shared/util/Preconditions");
    const Promise               = require("shared/util/Promise");
    const {Logger}              = require("shared/util/Logger");
    const WebFontLoader         = require("webfontloader");
    const WebFontsUtil          = require("shared/ui/experience/fonts/WebFontsUtil");

    const logger = new Logger("FontLoader");

    /**
     * Start the font loading using the wrapped <tt>WebFontLoader</tt>.
     *
     * If the current browser does not support web fonts (i.e. Google Bot), a
     * resolved promise is returned immediately.
     *
     * @param  {Object} config   Configuration containing the load information.
     * @return {Promise}         A promise that resolves when the fonts have been
     *                           successfully loaded.
     *
     * @see  https://github.com/typekit/webfontloader
     * @see  http://jira.zmags.com/browse/MOS-1483
     */
    function load(config) {
        if (!Browser.capabilities.isWebFontsSupported) {
            return Promise.resolved();
        }

        // Wraps the webfontloader logic in a promise
        const deferred = Promise.deferred();

        // We should really be waiting for all fonts to load, i.e. use the active callback. See
        // MOS-2490.
        config.fontactive = () => {
            deferred.resolve();
        };

        config.inactive = () => {
            deferred.reject(`Browser does not support linked fonts or none of the fonts could be loaded. Config: ${JSON.stringify(config)}`);
        };

        config.fontinactive = (familyName) => {
            deferred.reject(`Failed loading font family: ${familyName}. Failed loading config: ${JSON.stringify(config)}`);
        };

        WebFontLoader.load(config);

        return Promise.timeout(deferred, FontConstants.LOAD_FONTS_TIMEOUT, `Loading fonts is slow to respond. config: ${JSON.stringify(config)}`);
    }

    /**
     * A set of unique string values representing each font request. Used as a simple "caching" mechanism (guard) to
     * avoid loading the same font multiple times.
     *
     * @see #filterEntries
     *
     * @type {Set.<String>}
     */
    const entries = new Set();

    /**
     * Map used for loading fonts (the actual CSS needed to render) using the FontLoader.
     *
     * @type {Map.<FontProvider, Function>}
     */
    const fontLoaders = new Map();

    // Custom
    fontLoaders.set(FontProvider.CUSTOM, (fontDescriptors) => FontLoader.custom(fontDescriptors));

    // Google
    fontLoaders.set(FontProvider.GOOGLE, (fontDescriptors, loadVariants) => {
        let families;

        if (loadVariants) {
            families = WebFontsUtil.googleFontFamily(fontDescriptors);
        } else {
            families = _.map(fontDescriptors, (fontDescriptor) => fontDescriptor.get("family"));
        }

        return FontLoader.google(families);
    });

    // Typekit
    fontLoaders.set(FontProvider.TYPEKIT, (fontDescriptors) => {
        // Populate list of Typekit kit ID's (stored in the data container)
        const kitIDs = _.uniq(_.map(fontDescriptors, (fontDescriptor) => fontDescriptor.get("data").kitID));

        return FontLoader.typekit(kitIDs);
    });

    fontLoaders.set(FontProvider.FONTS_DOT_COM, (fontDescriptors) => {
        const projectIds = _.uniq(_.map(fontDescriptors, (fontDescriptor) => fontDescriptor.get("data").projectId));

        return FontLoader.fontsDotCom(projectIds, WebFontsUtil.getFontsDotComFamilies(fontDescriptors));
    });

    /**
     * Utility class for loading web fonts - wrapping
     * the <a href="https://github.com/typekit/webfontloader" target="_blank">Web Font Loader</a>.
     *
     * Includes a simple "caching" mechanism (guard) to avoid loading the same font multiple times.
     *
     * @class FontLoader
     * @author Kim
     */
    const FontLoader = Class.extend({}, {

        /**
         * Filter out already loaded fonts. Fonts are identified differently depending on the font
         * provider:
         *
         * Custom and Google Fonts: The name of the font.
         * Typekit: The Kit ID containing the font.
         * Fonts.com: The Project ID containing the font.
         *
         * Example: ["Open Sans", "Roboto", "Oswald", "eka2gcy", "tpf2man"] => ["Roboto", "eka2gcy"]
         *
         * @param {String[]} fontEntries
         *
         * @returns {String[]} Returns a new array containing the entries that has not yet been loaded.
         */
        filterOutAlreadyLoadedFonts(fontEntries) {
            return _.filter(fontEntries, (entry) => {
                if (entries.has(entry)) {
                    return false;
                }
                entries.add(entry);
                return true;
            });
        },

        /**
         * Start loading the fonts represented by the list of font descriptors.
         *
         * @param  {FontDescriptor[]} fontDescriptors Array of <tt>FontDescriptor</tt>'s.
         *
         * @param  {Boolean} [loadVariants] Whether to load all the variants associated to the
         *                                  specified font descriptors. Defaults to <tt>false</tt>.
         * @return {Promise} A promise that resolves when all font have been successfully loaded.
         */
        fromFontDescriptors(fontDescriptors, loadVariants) {
            const fontProviderToFontDescriptors = new Map();
            const promises = [];

            // Create a mapping between font providers and descriptors
            _.each(fontDescriptors, (fontDescriptor) => {
                const provider = fontDescriptor.get("provider");
                if (fontProviderToFontDescriptors.has(provider)) {
                    const list = fontProviderToFontDescriptors.get(provider);
                    list.push(fontDescriptor);
                } else {
                    fontProviderToFontDescriptors.set(provider, [fontDescriptor]);
                }
            });

            fontProviderToFontDescriptors.forEach((fontDescriptors, fontProvider) => {
                const loader = fontLoaders.get(fontProvider);
                promises.push(loader(fontDescriptors, loadVariants));
            });

            return Promise.all(promises);
        },

        /**
         * Loads all of the fonts used in the given experience.
         *
         * @param {ExperienceModel} experienceModel The experience.
         * @returns {Promise} A promise that resolves when all fonts are loaded.
         */
        loadExperienceFonts(experienceModel) {
            return FontLoader.loadSceneFonts(experienceModel.getAllScenes(), experienceModel);
        },

        /**
         * Loads all of the fonts used on the given scene or array of scenes.
         *
         * @param {Scene|Scene[]} scenes The scene or array of scenes to load.*
         * @param {ExperienceModel} experience experience model, used for logging
         * @returns {Promise} A promise that resolves when all fonts are loaded.
         */
        loadSceneFonts(scenes, experience) {
            const fontDescriptorCollection = WebFontsUtil.createFontDescriptorCollection(scenes);

            return FontLoader.loadFonts(fontDescriptorCollection, experience);
        },

        /**
         * Loads all fonts from the fonts descriptor collection.
         *
         * @param {FontDescriptorCollection} fontDescriptorCollection The fonts descriptor collection.
         * @param {ExperienceModel} experience experience model, used for logging
         * @returns {Promise} A promise that resolves when all fonts are loaded.
         */
        loadFonts(fontDescriptorCollection, experience) {
            const customFontsPromise = FontLoader.custom(fontDescriptorCollection.fromProvider(FontProvider.CUSTOM));
            const googleFontsPromise = FontLoader.google(WebFontsUtil.googleFontFamily(fontDescriptorCollection));
            const typekitPromise = FontLoader.typekit(WebFontsUtil.getTypekitKitIds(fontDescriptorCollection));
            const fontsDotComPromise = FontLoader.fontsDotCom(
                WebFontsUtil.getFontsDotComProjectIds(fontDescriptorCollection),
                WebFontsUtil.getFontsDotComFamilies(fontDescriptorCollection)
            );

            const fontLoadStartTimestamp = Date.now();
            let loadingDuration;
            return Promise.all([customFontsPromise, googleFontsPromise, typekitPromise, fontsDotComPromise]).always(() => {
                loadingDuration = Date.now() - fontLoadStartTimestamp;
            }).then(() => {
                const textToLog = [
                    `Font loading for experience Id ${experience.get("id")}, name: '${experience.get("name")}' finished successfully in ${loadingDuration}ms.`,
                    `Font Descriptor: ${FontLoader._generateFontDescriptorText(fontDescriptorCollection)}`
                ].join("\n");

                logger.info(textToLog);
            }).fail((reason) => {
                const textToLog = [
                    `Font loading failed for experience Id ${experience.get("id")}, name: '${experience.get("name")}' in ${loadingDuration}ms.`,
                    `Reason: '${reason}'.`,
                    `Font Descriptor (all providers): ${FontLoader._generateFontDescriptorText(fontDescriptorCollection)}`
                ].join("\n");

                logger.warn(textToLog);
            });
        },

        _generateFontDescriptorText(fontDescriptorCollection) {
            const fontDescriptorsWithoutSyncState = fontDescriptorCollection.map((fontDescriptor) => {
                const provider = fontDescriptor.get("provider");
                const providerLoggedObject = {};
                Object.keys(provider).forEach((key) => {
                    // filter out syncState as it throws a circular error when using toJSON()
                    if (key !== "syncState") {
                        providerLoggedObject[key] = provider[key];
                    }
                });

                const fontDescriptorObject = $.extend(true, {}, fontDescriptor.toJSON());

                fontDescriptorObject.provider = providerLoggedObject; // remove syncState circular reference

                return fontDescriptorObject;
            });

            return JSON.stringify(fontDescriptorsWithoutSyncState);
        },
        /**
         * Loads the specified array of custom font families.
         *
         * @param  {FontDescriptor[]} fontDescriptors Array of custom `FontDescriptor`'s.
         *
         * @return {Promise} A promise that resolves when loading is complete.
         */
        custom(fontDescriptors) {
            Preconditions.checkArgument(fontDescriptors);

            const families = this.filterOutAlreadyLoadedFonts(fontDescriptors.map((fontDescriptor) => fontDescriptor.get("family")));

            if (_.isEmpty(families)) {
                return Promise.resolved();
            }

            WebFontsUtil.injectFontFaceCSS(fontDescriptors);

            return load({
                custom: {
                    families
                }
            });
        },

        /**
         * Loads the specified Typekit kit(s).
         *
         * Note: Due to a bug in the webfontloader/typekit, it's only possible to load a single
         * kit via the webfontloader ( https://github.com/typekit/webfontloader/issues/152 ). It
         * seems that the kit concept is tied to a single kit/package per domain/site.
         *
         * The below logic circumvents this issue.
         *
         * @param  {String|String[]} kits The raw kit ID or an array of kit ID's.
         * @return {Promise} A promise that resolves when all kit's have been fetched.
         */
        typekit(kits) {
            Preconditions.checkArgument(kits);

            function kit(id) {
                Preconditions.checkArgument(id);

                return load({typekit: {id}});
            }

            kits = this.filterOutAlreadyLoadedFonts(_.isString(kits) ? [kits] : kits);

            if (_.isEmpty(kits)) {
                return Promise.resolved();
            }

            // Hack: It's important that the kit's are loaded in sequence, not in parallel.
            return Promise.deferred((deferred) => {
                let when = $.when(kit(kits.shift()));

                while (kits.length) {
                    when = when.then(kit.bind(this, kits.shift()));
                }

                when.done(deferred.resolve).fail(deferred.reject);
            });
        },

        /**
         * Loads the specified array of Google font families.
         *
         * @param  {String[]} families Array of Google font
         * families, e.g. <tt>["Just Another Hand:regular", "Roboto Condensed:700italic"]</tt>.
         *
         * @return {Promise} A promise that resolves when loading is complete.
         */
        google(families) {
            Preconditions.checkArgument(families);

            families = this.filterOutAlreadyLoadedFonts(families);

            if (_.isEmpty(families)) {
                return Promise.resolved();
            }

            return load({
                google: {
                    families
                }
            });
        },

        /**
         * Loads the specified Fonts.com project(s).
         *
         * This class has a ViewerMixin for this method to load fonts from fonts.com directly instead of using Webfontloader
         *
         * @param {String|String[]} projectIds A Fonts.com project ID (a GUID) or an array of project IDs.
         * @param  {String[]} families Array of Fonts.com font
         * families, e.g. <tt>["Just Another Hand:regular", "Roboto Condensed:700italic"]</tt>.
         *
         * @return {Promise} A promise that resolves when all projects have been fetched.
         */
        fontsDotCom(projectIds) {
            if (_.isString(projectIds)) {
                projectIds = [projectIds];
            }

            _.each(projectIds, (projectId) => {
                if (_.isEmpty(projectId)) {
                    Preconditions.fail("projectId is not defined.");
                }
            });

            const projectsIdsToLoad = this.filterOutAlreadyLoadedFonts(projectIds);

            if (_.isEmpty(projectsIdsToLoad)) {
                return Promise.resolved();
            }

            const project = (projectId) => load({
                monotype: {
                    loadAllFonts: true,
                    projectId
                }
            });

            const loadFontProjects = _.map(projectsIdsToLoad, (projectId) => project(projectId));
            return Promise.all(loadFontProjects);
        }
    });

    return FontLoader;
});



// WEBPACK FOOTER //
// ./src/shared/util/FontLoader.js